import { UserRoleAssignmentSortOption } from '@rematter/frontend-lib-api-client';

import { createQueryHook } from '~/shared/providers/api';

export interface ExternalRoleUserRecord {
  id: string;
  assignmentId: string;
  name: string;
  roles: string[];
  email: string;
  phoneNumber: string | null;
  facilities: string[];
  groups: string[];
  status: 'active' | 'archived';
}

export interface ExternalRoleUsersQueryParams {
  roleId: string;
  facilityIds?: string[];
  sorting?: UserRoleAssignmentSortOption[];
  limit?: number;
  offset?: number;
}

export interface ExternalRoleUsersQueryResult {
  users: ExternalRoleUserRecord[];
  total: number;
}

export const useExternalRoleUsersQuery = createQueryHook(
  'settings.customerPortal.roleUsers',
  (params: ExternalRoleUsersQueryParams) => ({
    coreUserRoleAssignments: {
      __args: {
        filter: {
          roleId: params.roleId,
          facilityIds: params.facilityIds,
          sorting: params.sorting,
          pagination: {
            limit: params.limit ?? 20,
            offset: params.offset ?? 0,
          },
        },
      },
      entries: {
        id: true,
        userId: true,
        user: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
          phoneNumber: true,
          status: true,
          facilities: {
            id: true,
            facilityName: true,
          },
        },
        role: {
          id: true,
          name: true,
        },
        facility: {
          id: true,
          facilityName: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): ExternalRoleUsersQueryResult => {
      const entries = data?.coreUserRoleAssignments?.entries ?? [];

      const userMap = new Map<string, ExternalRoleUserRecord>();

      entries.forEach((assignment) => {
        const user = assignment.user;
        if (!user) return;

        const existingUser = userMap.get(user.id);
        if (existingUser) {
          if (
            assignment.facility?.facilityName &&
            !existingUser.facilities.includes(assignment.facility.facilityName)
          ) {
            existingUser.facilities.push(assignment.facility.facilityName);
          }
          if (
            assignment.role?.name &&
            !existingUser.roles.includes(assignment.role.name)
          ) {
            existingUser.roles.push(assignment.role.name);
          }
        } else {
          userMap.set(user.id, {
            id: user.id,
            assignmentId: assignment.id,
            name:
              `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || '-',
            roles: assignment.role?.name ? [assignment.role.name] : [],
            email: user.email ?? '',
            phoneNumber: user.phoneNumber ?? null,
            facilities: assignment.facility?.facilityName
              ? [assignment.facility.facilityName]
              : [],
            groups: [],
            status: user.status === 'ARCHIVED' ? 'archived' : 'active',
          });
        }
      });

      return {
        users: Array.from(userMap.values()),
        total: userMap.size,
      };
    },
  },
);
